/**
 * Backend/DataSource on top of the real API, 1:1 to docs/api.md. Every
 * method is one route (photos: three steps per photo); what the contract
 * lists under "Abweichungen vom DESIGN.md-Backlog" is absorbed here, so no
 * screen knows whether it talks to the demo store or the server:
 *
 *  - Drawings are authenticated routes. They are fetched with the Bearer
 *    token and shown through blob: URLs, cached per type and view for the
 *    session and revoked by dispose() when the session ends. Types without a
 *    bundled drawing (`placeholder: true`) get the built-in silhouette.
 *  - Photos: per image upload-url → PUT image → PUT thumbnail → register,
 *    one after the other, with progress. A refused photo is reported and the
 *    next one is tried; only when none made it does the call throw.
 *  - removeImage/restoreImage need the damage id in the path; the adapter
 *    remembers it from every response that listed the photo.
 *  - Errors become DataError with the API's status and message; a 409 with
 *    code 'conflict' (the updated_at guard) becomes code 'stale' and the
 *    write hook reloads.
 *
 * Used only for a real session (backendFactory.ts); every call asserts that,
 * so a demo session can never reach the server through this module.
 */
import { ALL_VIEWS, BASE_VIEWS, CABIN_VIEW, PINS_DRIFT, de, type AircraftView, type BaseView } from '@fsv/core';
import { ApiError, type ApiClient, type RequestOptions } from '@fsv/shared-client';
import { cabinPlaceholder, placeholderDrawing } from '../drawings/placeholder';
import { tx } from '../i18n/extra';
import { toSearchParams } from '../lib/search';
import { DataError, PhotoUploadError, type Backend, type DataSource, type PlatformSource } from './DataSource';
import type {
  AddImagesResult,
  Aircraft,
  AircraftNotice,
  AircraftType,
  ChangeEvent,
  ClubContact,
  Damage,
  DamageComment,
  DamageDetail,
  DamageImage,
  Drawing,
  DrawingUploadResult,
  EventFilter,
  ExportLink,
  FleetCounts,
  Billing,
  ExportResult,
  FleetAircraft,
  FleetAircraftType,
  FleetEntry,
  FleetView,
  ImageUploadFailure,
  ImportSummary,
  InboxEntry,
  Me,
  Member,
  MyEmails,
  NewImage,
  Settings,
  Tenant,
  TenantAdminRef,
  Contract,
  TenantDetail,
  TrashItem,
  UploadProgress,
  WriteOptions,
  WriteResult,
} from './types';
import type { AnnouncementRef } from '../news/news';

export interface ApiBackendOptions {
  client: ApiClient;
  /** True while the signed-in session is a real one; checked before every call. */
  isRealSession: () => boolean;
  /** For the blob PUTs and the drawings; defaults to the global fetch, looked up per call. */
  fetchImpl?: typeof fetch;
  /** How an authenticated drawing becomes something an image view can show. Default: blob: URLs (browser). */
  drawings?: DrawingTransport;
  /** How a prepared photo reaches its SAS URL. Default: data URL → Blob → fetch PUT (browser). */
  photos?: PhotoTransport;
}

/**
 * Drawings are authenticated routes, so an image view cannot load them by
 * URL. The browser fetches them with the token and hands out blob: URLs; the
 * native app downloads them into its cache directory and hands out file:
 * URIs. Either way the session's copies are dropped by dispose().
 */
export interface DrawingTransport {
  /** `url` is absolute; resolves to a URL an image view can show, or null when the drawing could not be fetched. */
  load(url: string, token: string | null): Promise<string | null>;
  /** Drops one URL handed out by load() (a replaced drawing); optional for transports that cache by file. */
  release?(url: string): void;
  dispose(): void;
}

/**
 * One PUT of one prepared JPEG to a write SAS. `source` is what the client put
 * into NewImage.dataUrl / thumbDataUrl: a data: URL on the web, a file: URI of
 * the re-encoded JPEG on the native app. Resolves with the HTTP status;
 * rejects when the request never reached the storage account.
 */
export interface PhotoTransport {
  /** PUTs the bytes behind `source`; `onProgress` gets 0…1 when the platform reports upload progress. */
  put(url: string, source: string, headers: Record<string, string>, onProgress?: (fraction: number) => void): Promise<number>;
}

/** Browser drawings: authenticated fetch → blob: URL, revoked on dispose(). */
export function browserDrawingTransport(fetchImpl: typeof fetch = (input, init) => fetch(input, init)): DrawingTransport {
  const objectUrls = new Set<string>();
  return {
    async load(url, token) {
      try {
        const res = await fetchImpl(url, { headers: { Accept: 'image/png', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
        if (!res.ok) return null;
        const objectUrl = URL.createObjectURL(await res.blob());
        objectUrls.add(objectUrl);
        return objectUrl;
      } catch {
        return null;
      }
    },
    release(url) {
      if (objectUrls.delete(url)) URL.revokeObjectURL(url);
    },
    dispose() {
      for (const u of objectUrls) URL.revokeObjectURL(u);
      objectUrls.clear();
    },
  };
}

/**
 * Browser photos and videos: a data URL is turned into a Blob without fetch, a
 * blob: URL (a prepared video) is read back; both are PUT as the body. With
 * `onProgress` the PUT goes through XMLHttpRequest, the one browser API that
 * reports upload progress — a 20 MB video on a weak line needs it.
 */
export function fetchPhotoTransport(fetchImpl: typeof fetch = (input, init) => fetch(input, init)): PhotoTransport {
  return {
    async put(url, source, headers, onProgress) {
      const body = source.startsWith('blob:') ? await (await fetchImpl(source)).blob() : dataUrlToBlob(source);
      if (!onProgress || typeof XMLHttpRequest === 'undefined') {
        const res = await fetchImpl(url, { method: 'PUT', headers, body });
        return res.status;
      }
      return new Promise<number>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
        };
        xhr.onload = () => resolve(xhr.status);
        xhr.onerror = () => reject(new Error('network'));
        xhr.onabort = () => reject(new Error('aborted'));
        xhr.send(body);
      });
    },
  };
}

/** Answer of POST …/images/upload-url. */
interface UploadSlot {
  uuid: string;
  uploadUrl: string;
  thumbUploadUrl: string;
  expiresAt: string;
  /** For the main blob (image/jpeg, or video/mp4 since 0018). */
  headers?: Record<string, string>;
  /** For the thumbnail / poster; an older API sends none (then the main headers, both JPEG). */
  thumbHeaders?: Record<string, string>;
}

/** `/t/{tenantId}/aircraft-types` before the drawings are resolved. */
type RawAircraftType = Omit<AircraftType, 'drawings'> & { drawings: Partial<Record<AircraftView, Drawing>> };

/** Answer of POST …/drawings/{view}/upload-url. */
interface DrawingSlot {
  uuid: string;
  uploadUrl: string;
  expiresAt: string;
  headers?: Record<string, string>;
}

export type ApiBackend = Backend & { readonly kind: 'api'; dispose(): void };

/** ApiError → DataError with the semantics the screens know. Anything else passes unchanged. */
export function toDataError(error: unknown): unknown {
  if (!(error instanceof ApiError)) return error;
  switch (error.status) {
    case 0:
      return new DataError(0, de.errors.offline, 'offline');
    case 401:
      return new DataError(401, tx.errors.sessionEnded);
    case 404:
      return new DataError(404, de.errors.notFound);
    case 409:
      // code 'conflict' = changed since read (packages/api/src/utils/errors.ts, conflictError()); other 409s keep their message.
      // 'pins_drift' keeps its code and text: it is a question for the person (force), not a stale read.
      return error.code === 'conflict' ? new DataError(409, tx.errors.stale, 'stale') : new DataError(409, error.message, error.code);
    case 400:
    case 403:
    case 503:
      return new DataError(error.status, error.message, error.code);
    case 413:
      return new DataError(400, error.message, error.code);
    default:
      return error;
  }
}

/** data:image/jpeg;base64,… → Blob, without fetch (a data: fetch is still a fetch). */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  const meta = dataUrl.slice(0, comma);
  const payload = dataUrl.slice(comma + 1);
  const type = /^data:([^;,]+)/.exec(meta)?.[1] ?? 'application/octet-stream';
  if (!meta.endsWith(';base64')) return new Blob([decodeURIComponent(payload)], { type });
  const bin = atob(payload);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

const undoBody = (opts?: WriteOptions) => (opts?.undoOf !== undefined ? { undoOf: opts.undoOf } : {});
const undoQuery = (opts?: WriteOptions) => (opts?.undoOf !== undefined ? `?undoOf=${encodeURIComponent(String(opts.undoOf))}` : '');
const seg = (v: string) => encodeURIComponent(v);
/** Only the fields that are set: a PATCH names what changes, nothing else. */
const definedOnly = <T extends object>(o: T): Partial<T> => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;

/** A club's export is built during the request (src/export): give it the API's own limit, not the client default. */
const EXPORT_TIMEOUT_MS = 10 * 60_000;

export function createApiBackend(options: ApiBackendOptions): ApiBackend {
  const { client } = options;
  const doFetch: typeof fetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const drawings = options.drawings ?? browserDrawingTransport(doFetch);
  const photos = options.photos ?? fetchPhotoTransport(doFetch);

  async function call<T>(path: string, init: RequestOptions = {}): Promise<T> {
    if (!options.isRealSession()) {
      // Never reached by design (backendFactory.ts); if it ever is, fail loudly instead of mixing sessions.
      throw new Error('API backend used outside a real session');
    }
    try {
      return await client<T>(path, init);
    } catch (e) {
      throw toDataError(e);
    }
  }

  /** A text answer (CSV): the client only speaks JSON, so this one goes by fetch with the session token. */
  async function text(path: string): Promise<string> {
    if (!options.isRealSession()) throw new Error('API backend used outside a real session');
    const token = client.getToken();
    let res: Response;
    try {
      res = await doFetch(`${client.baseUrl}${path}`, { headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    } catch {
      throw new DataError(0, de.errors.offline, 'offline');
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw toDataError(new ApiError(res.status, body.error ?? `Fehler (${res.status})`));
    }
    return res.text();
  }

  // ------------------------------------------------------------------ drawings
  const drawingCache = new Map<string, Promise<string | null>>();

  function fetchDrawing(relativeUrl: string): Promise<string | null> {
    return drawings.load(`${client.baseUrl}${relativeUrl}`, client.getToken());
  }

  /** One authenticated drawing as a showable URL, fetched once per key; null when it could not be fetched. */
  async function cachedDrawing(key: string, relativeUrl: string): Promise<string | null> {
    let pending = drawingCache.get(key);
    if (!pending) {
      pending = fetchDrawing(relativeUrl);
      drawingCache.set(key, pending);
    }
    const url = await pending;
    if (!url) drawingCache.delete(key); // try again on the next read
    return url;
  }

  /**
   * A replaced drawing keeps its URL but not its content (new ETag): after
   * every fleet write the type's cached copies are dropped and released, so
   * the next read fetches the new file (docs/fleet-admin-ui.md, section 3).
   */
  function forgetType(typeId: string): void {
    for (const [key, pending] of [...drawingCache]) {
      if (!key.startsWith(`${typeId}/`) && !key.startsWith(`fleet:${typeId}/`)) continue;
      drawingCache.delete(key);
      void pending.then((u) => {
        if (u) drawings.release?.(u);
      });
    }
  }

  async function resolveDrawing(type: RawAircraftType, view: AircraftView, base: BaseView): Promise<Drawing> {
    const d = type.drawings[view];
    // The cabin's stand-in is the cabin schematic, never a silhouette of the outside.
    const standIn = () => (view === CABIN_VIEW ? cabinPlaceholder(type.code) : placeholderDrawing(type.code, base));
    if (!d || d.placeholder || !d.url) return standIn();
    const url = await cachedDrawing(`${type.id}/${view}`, d.url);
    // The silhouette stands in until the next read succeeds.
    if (!url) return standIn();
    return { url, width: d.width, height: d.height, placeholder: false };
  }

  async function resolveType<T extends RawAircraftType>(type: T): Promise<Omit<T, 'drawings'> & Pick<AircraftType, 'drawings'>> {
    // The base views always; a derived view only when the club gave it its own image.
    const views = ALL_VIEWS.filter((v) => (BASE_VIEWS as readonly string[]).includes(v) || type.drawings[v] !== undefined);
    const entries = await Promise.all(
      views.map(async (v) => [v, await resolveDrawing(type, v, (BASE_VIEWS as readonly string[]).includes(v) ? (v as BaseView) : 'oben')] as const),
    );
    return { ...type, drawings: Object.fromEntries(entries) as AircraftType['drawings'] };
  }

  /** The fleet editor's tiles: every image the API holds, also before the type is complete. */
  async function resolveFleetType(type: FleetAircraftType): Promise<FleetAircraftType> {
    const resolved = await resolveType(type as unknown as RawAircraftType & FleetAircraftType);
    const views = await Promise.all(
      ALL_VIEWS.map(async (v): Promise<[AircraftView, FleetView]> => {
        // An API from before the cabin view (innen) does not list it: a missing tile, not a crash.
        const fv: FleetView = type.views[v] ?? { source: 'missing', mirroredFrom: null, url: '', width: null, height: null, uploadId: null };
        if (!fv.url) return [v, fv];
        const url = await cachedDrawing(`fleet:${type.id}/${v}/${fv.uploadId ?? 'bundled'}`, fv.url);
        return [v, { ...fv, url: url ?? '' }];
      }),
    );
    return { ...(resolved as unknown as FleetAircraftType), views: Object.fromEntries(views) as Record<AircraftView, FleetView> };
  }

  // ------------------------------------------------------------------ photos
  /** Damage of each photo id seen in a response, for the photo routes that need it in the path. */
  const imageDamage = new Map<string, string>();
  const rememberImages = (images: readonly DamageImage[]) => {
    for (const i of images) imageDamage.set(i.id, i.damageId);
  };

  async function put(url: string, source: string, headers: Record<string, string>, onProgress?: (fraction: number) => void): Promise<void> {
    let status: number;
    try {
      status = await photos.put(url, source, headers, onProgress);
    } catch {
      throw new DataError(0, de.errors.offline, 'offline');
    }
    if (status < 200 || status >= 300) throw new DataError(status === 403 ? 403 : 503, tx.upload.storageRefused(status));
  }

  async function uploadImages(
    base: string,
    damageId: string,
    images: readonly NewImage[],
    mergedReport: { note: string | null } | undefined,
    onProgress: ((p: UploadProgress) => void) | undefined,
  ): Promise<{ created: DamageImage[]; eventIds: number[]; failures: ImageUploadFailure[] }> {
    const created: DamageImage[] = [];
    const eventIds: number[] = [];
    const failures: ImageUploadFailure[] = [];
    onProgress?.({ done: 0, total: images.length });
    for (const [index, img] of images.entries()) {
      try {
        const video = img.kind === 'video';
        const slot = await call<UploadSlot>(`${base}/damages/${seg(damageId)}/images/upload-url`, video ? { method: 'POST', body: { kind: 'video' } } : { method: 'POST' });
        const jpeg = { 'x-ms-blob-type': 'BlockBlob', 'x-ms-blob-content-type': 'image/jpeg' };
        const headers = { ...jpeg, ...(slot.headers ?? {}) };
        // The poster first: small, and a video that never finishes leaves only a stray thumbnail for the orphan sweep.
        await put(slot.thumbUploadUrl, img.thumbDataUrl, { ...jpeg, ...(slot.thumbHeaders ?? slot.headers ?? {}) });
        // Byte progress only for videos: a photo is sent in a moment, and fetch keeps its simpler path.
        await put(slot.uploadUrl, img.dataUrl, headers, video ? (fraction) => onProgress?.({ done: index, total: images.length, fraction }) : undefined);
        const r = await call<WriteResult<DamageImage[]>>(`${base}/damages/${seg(damageId)}/images`, {
          method: 'POST',
          // A video's registration reads the whole file on the server: more time than a photo's.
          ...(video ? { timeoutMs: 120_000 } : {}),
          body: { uuid: slot.uuid, width: img.width, height: img.height, isOverview: video ? false : img.isOverview, ...(video ? { kind: 'video' } : {}), ...(mergedReport ? { mergedReport } : {}) },
        });
        rememberImages(r.value);
        for (const v of r.value) {
          created.push(v);
          eventIds.push(r.eventId);
        }
      } catch (e) {
        // A 401 ends the session anyway; everything else is this photo's problem only.
        if (e instanceof DataError && e.status === 401) throw e;
        failures.push({
          index,
          message: e instanceof Error && e.message ? e.message : de.errors.generic,
          ...(e instanceof DataError ? { status: e.status } : {}),
        });
      }
      onProgress?.({ done: index + 1, total: images.length });
    }
    return { created, eventIds, failures };
  }

  // ------------------------------------------------------------------ tenant source
  const sources = new Map<string, DataSource>();

  function createTenantSource(tenantId: string): DataSource {
    const base = `/t/${seg(tenantId)}`;
    const get = <T>(path: string) => call<T>(`${base}${path}`);
    const send = <T>(method: NonNullable<RequestOptions['method']>, path: string, body?: unknown) =>
      call<T>(`${base}${path}`, body === undefined ? { method } : { method, body });

    function damageOfImage(imageId: string): string {
      const damageId = imageDamage.get(imageId);
      // The screens only offer photo actions on photos they were shown, so this is a programming error.
      if (!damageId) throw new DataError(404, de.errors.notFound);
      return damageId;
    }

    return {
      kind: 'api',
      tenantId,

      getSettings: () => get<Settings>('/settings'),
      async listAircraftTypes() {
        const raw = await get<RawAircraftType[]>('/aircraft-types');
        return Promise.all(raw.map(resolveType));
      },
      listFleet: () => get<FleetEntry[]>('/aircraft?summary=1'),
      listAircraft: () => get<Aircraft[]>('/aircraft'),
      listDamages: (registration) => get<Damage[]>(`/aircraft/${seg(registration)}/damages`),
      async getDamage(id) {
        const detail = await get<DamageDetail>(`/damages/${seg(id)}`);
        rememberImages(detail.images);
        return detail;
      },
      search(query) {
        const qs = toSearchParams(query).toString();
        return get<Damage[]>(`/search${qs ? `?${qs}` : ''}`);
      },
      inbox: () => get<InboxEntry[]>('/inbox'),
      myReports: () => get<Damage[]>('/me/reports'),
      listMembers: () => get<Member[]>('/manage/members'),
      listEvents(filter: EventFilter) {
        const p = new URLSearchParams();
        if (filter.entityType) p.set('entity', filter.entityType);
        if (filter.actorId) p.set('actor', filter.actorId);
        if (filter.action) p.set('action', filter.action);
        if (filter.from) p.set('from', filter.from);
        if (filter.to) p.set('to', filter.to);
        const qs = p.toString();
        return get<ChangeEvent[]>(`/manage/events${qs ? `?${qs}` : ''}`);
      },
      async listTrash() {
        const items = await get<TrashItem[]>('/manage/trash');
        for (const i of items) if (i.kind === 'damage_image' && i.damageId) imageDamage.set(i.id, i.damageId);
        return items;
      },

      async createDamage(input, upload) {
        const { images, status, mediaCount, ...rest } = input;
        // Tells the club's photo rule what follows (media.ts).
        const body = { ...rest, mediaCount: mediaCount ?? images.length };
        // A member never sends a status (the server answers 403 forbidden_field for anything but 'gemeldet').
        const created = await send<WriteResult<Damage>>('POST', '/damages', status === undefined ? body : { ...body, status });
        if (images.length === 0) return { ...created, imageFailures: [] };
        const { failures } = await uploadImages(base, created.value.id, images, undefined, upload?.onProgress);
        return { ...created, imageFailures: failures };
      },
      updateDamage: (id, patch, opts) =>
        send('PATCH', `/damages/${seg(id)}`, { ...patch, ...undoBody(opts), ...(opts?.expectedUpdatedAt ? { updatedAt: opts.expectedUpdatedAt } : {}) }),
      reviewDamage: (id, input) => send('POST', `/damages/${seg(id)}/review`, input),
      revertReview: (id, eventId) => send('POST', `/damages/${seg(id)}/review/${seg(String(eventId))}/undo`),
      deleteDamage: (id, opts) => send('DELETE', `/damages/${seg(id)}${undoQuery(opts)}`),
      restoreDamage: (id, opts) => send('POST', `/damages/${seg(id)}/restore`, undoBody(opts)),

      async addImages(damageId, images, opts): Promise<AddImagesResult> {
        // undoOf is refused by the register route (400); an undo of a removal is restoreImage.
        const { created, eventIds, failures } = await uploadImages(base, damageId, images, opts?.mergedReport, opts?.onProgress);
        if (created.length === 0) {
          throw new PhotoUploadError(failures.length === 1 ? (failures[0]?.message ?? de.errors.generic) : tx.upload.noneUploaded, failures);
        }
        return { value: created, eventId: eventIds[0] ?? 0, eventIds, failures };
      },
      async removeImage(imageId, opts) {
        const r = await send<WriteResult<DamageImage>>('DELETE', `/damages/${seg(damageOfImage(imageId))}/images/${seg(imageId)}${undoQuery(opts)}`);
        rememberImages([r.value]);
        return r;
      },
      async restoreImage(imageId, opts) {
        const r = await send<WriteResult<DamageImage>>('POST', `/damages/${seg(damageOfImage(imageId))}/images/${seg(imageId)}/restore`, undoBody(opts));
        rememberImages([r.value]);
        return r;
      },
      async listNotices() {
        try {
          return await get<AircraftNotice[]>('/notices');
        } catch (e) {
          // An API from before 0022 has no route: no notices, not a broken page.
          if (e instanceof DataError && e.status === 404) return [];
          throw e;
        }
      },
      listNoticeHistory: (aircraftId) => get<AircraftNotice[]>(`/notices?aircraftId=${seg(aircraftId)}&all=1`),
      createNotice: (input) => send<WriteResult<AircraftNotice>>('POST', '/notices', input),
      updateNotice: (id, patch, opts) =>
        send<WriteResult<AircraftNotice>>('PATCH', `/notices/${seg(id)}`, {
          ...patch,
          ...(opts?.expectedUpdatedAt ? { updatedAt: opts.expectedUpdatedAt } : {}),
        }),
      resolveNotice: (id) => send<WriteResult<AircraftNotice>>('POST', `/notices/${seg(id)}/resolve`),
      acknowledgeNotice: (id) => send<AircraftNotice>('POST', `/notices/${seg(id)}/ack`),
      addComment: (damageId, body) => send<WriteResult<DamageComment>>('POST', `/damages/${seg(damageId)}/comments`, { body }),
      deleteComment: (commentId, opts) => send('DELETE', `/comments/${seg(commentId)}${undoQuery(opts)}`),
      restoreComment: (commentId, opts) => send('POST', `/comments/${seg(commentId)}/restore`, undoBody(opts)),

      // Exactly these three fields: the server refuses `status` (and any other field) with 403 forbidden_field.
      addMember: (input) => send<WriteResult<Member>>('POST', '/manage/members', { email: input.email, displayName: input.displayName, role: input.role }),
      updateMember: (id, patch, opts) => send('PATCH', `/manage/members/${seg(id)}`, { ...patch, ...undoBody(opts) }),
      importMembers: (rows, deactivateMissing, deactivate = [], invite = false) =>
        send<WriteResult<ImportSummary>>('POST', '/manage/members/import', { rows, deactivateMissing, ...(deactivate.length ? { deactivate } : {}), invite }),
      inviteMember: (id) => send<Member>('POST', `/manage/members/${seg(id)}/invite`),
      async endMemberSessions(id) {
        await send<void>('POST', `/manage/members/${seg(id)}/signout-all`);
      },
      setAircraftActive: (id, active, opts) => send('PATCH', `/manage/aircraft/${seg(id)}`, { isActive: active, ...undoBody(opts) }),
      updateSettings: (patch, opts) => send('PUT', '/manage/settings', { ...patch, ...undoBody(opts) }),

      getClub: () => get<Tenant>(''),
      getClubContact: () => get<ClubContact>('/contact'),
      updateClub: (patch, opts) => send('PATCH', '', { ...patch, ...undoBody(opts) }),
      setClubLogo: (pngBase64) => send('PUT', '/manage/logo', { png: pngBase64 }),
      removeClubLogo: () => send('DELETE', '/manage/logo'),
      changeMemberEmail: (id, email) => send<WriteResult<Member>>('PUT', `/manage/members/${seg(id)}/email`, { email }),
      addMemberEmail: (id, email) => send<WriteResult<Member>>('POST', `/manage/members/${seg(id)}/emails`, { email }),
      removeMemberEmail: (id, emailId) => send<WriteResult<Member>>('DELETE', `/manage/members/${seg(id)}/emails/${seg(emailId)}`),
      anonymizeMember: (id) => send<WriteResult<Member>>('POST', `/manage/members/${seg(id)}/anonymize`),

      // ---------------------------------------------------------------- fleet
      listFleetAdmin: () => get<FleetAircraft[]>('/manage/aircraft'),
      createAircraft: (input) =>
        send<WriteResult<FleetAircraft>>('POST', '/manage/aircraft', {
          registration: input.registration,
          typeId: input.typeId,
          ...(input.sortOrder !== undefined ? { sortOrder: input.sortOrder } : {}),
        }),
      // Only the fields that are set, plus force after the drift question. A type change moves
      // the aircraft to other drawings; the drawings themselves (and their cache) stay as they are.
      updateAircraft: (id, patch, opts) =>
        send<WriteResult<FleetAircraft>>('PATCH', `/manage/aircraft/${seg(id)}`, {
          ...definedOnly(patch),
          ...(opts?.force ? { force: true } : {}),
          ...undoBody(opts),
        }),
      deleteAircraft: (id, opts) => send('DELETE', `/manage/aircraft/${seg(id)}${undoQuery(opts)}`),
      restoreAircraft: (id, opts) => send('POST', `/manage/aircraft/${seg(id)}/restore`, undoBody(opts)),
      async listFleetTypes() {
        const raw = await get<FleetAircraftType[]>('/manage/aircraft-types');
        return Promise.all(raw.map(resolveFleetType));
      },
      async createAircraftType(input) {
        const r = await send<WriteResult<FleetAircraftType>>('POST', '/manage/aircraft-types', { code: input.code, name: input.name, credit: input.credit, aircraftClass: input.aircraftClass });
        return { ...r, value: await resolveFleetType(r.value) };
      },
      async updateAircraftType(id, patch, opts) {
        // Never the code: the server refuses it (400), a type's code is fixed.
        const r = await send<WriteResult<FleetAircraftType>>('PATCH', `/manage/aircraft-types/${seg(id)}`, { ...definedOnly({ name: patch.name, credit: patch.credit, aircraftClass: patch.aircraftClass }), ...undoBody(opts) });
        return { ...r, value: await resolveFleetType(r.value) };
      },
      async uploadDrawing(typeId, view, file, opts) {
        const total = 3;
        opts?.onProgress?.({ done: 0, total });
        const path = `/manage/aircraft-types/${seg(typeId)}/drawings/${seg(view)}`;
        const slot = await send<DrawingSlot>('POST', `${path}/upload-url`);
        opts?.onProgress?.({ done: 1, total });
        let status: number;
        try {
          const res = await doFetch(slot.uploadUrl, {
            method: 'PUT',
            headers: { 'x-ms-blob-type': 'BlockBlob', 'x-ms-blob-content-type': 'image/png', ...(slot.headers ?? {}) },
            body: file.blob,
          });
          status = res.status;
        } catch {
          throw new DataError(0, de.errors.offline, 'offline');
        }
        if (status < 200 || status >= 300) throw new DataError(status === 403 ? 403 : 503, tx.upload.drawingStorageRefused(status));
        opts?.onProgress?.({ done: 2, total });
        // Width and height are never sent: the server reads them from the PNG (400 otherwise).
        const r = await registerDrawing(typeId, view, slot.uuid, opts);
        opts?.onProgress?.({ done: total, total });
        return r;
      },
      confirmDrawing: (typeId, view, uploadId, opts) => registerDrawing(typeId, view, uploadId, opts),

      // ---------------------------------------------------------------- export
      // The archive is built while the request waits: minutes, not the usual timeout.
      createExport: () => call<WriteResult<ExportResult>>(`${base}/manage/export`, { method: 'POST', timeoutMs: EXPORT_TIMEOUT_MS }),
      listExports: () => get<ExportLink[]>('/manage/export'),
    };

    async function registerDrawing(typeId: string, view: AircraftView, uuid: string, opts?: { force?: boolean | undefined; undoOf?: number | undefined }): Promise<DrawingUploadResult> {
      try {
        const r = await send<DrawingUploadResult>('PUT', `/manage/aircraft-types/${seg(typeId)}/drawings/${seg(view)}`, {
          uuid,
          ...(opts?.force ? { force: true } : {}),
          ...undoBody(opts),
        });
        forgetType(typeId);
        return { ...r, warnings: r.warnings ?? [], value: await resolveFleetType(r.value) };
      } catch (e) {
        // The blob stays on the server; confirmDrawing(…, uuid, { force }) takes it without a second upload.
        if (e instanceof DataError && e.code === PINS_DRIFT) throw new DataError(409, e.message, PINS_DRIFT, { uploadId: uuid });
        throw e;
      }
    }
  }

  // ------------------------------------------------------------------ platform
  const platform: PlatformSource = {
    listTenants: () => call<Tenant[]>('/platform/tenants'),
    getTenant: (id) => call<TenantDetail>(`/platform/tenants/${seg(id)}`),
    createTenant: (input) => call<WriteResult<Tenant>>('/platform/tenants', { method: 'POST', body: input }),
    updateTenant: (id, patch) => call<WriteResult<Tenant>>(`/platform/tenants/${seg(id)}`, { method: 'PATCH', body: patch }),
    addTenantAdmin: (id, admin) => call<WriteResult<TenantAdminRef>>(`/platform/tenants/${seg(id)}/admins`, { method: 'POST', body: admin }),
    createTenantExport: (id) => call<WriteResult<ExportResult>>(`/platform/tenants/${seg(id)}/export`, { method: 'POST', timeoutMs: EXPORT_TIMEOUT_MS }),
    listTenantExports: (id) => call<ExportLink[]>(`/platform/tenants/${seg(id)}/export`),
    // The contract routes answer the contract itself; no change event of a club records them (0024).
    listContracts: () => call<Contract[]>('/platform/contracts'),
    getContract: (id) => call<Contract>(`/platform/contracts/${seg(id)}`),
    createContract: async (input) => ({ value: await call<Contract>('/platform/contracts', { method: 'POST', body: input }), eventId: 0 }),
    updateContract: async (id, patch) => ({ value: await call<Contract>(`/platform/contracts/${seg(id)}`, { method: 'PATCH', body: patch }), eventId: 0 }),
    addContractDepartment: async (id, tenantId) => ({
      value: await call<Contract>(`/platform/contracts/${seg(id)}/departments`, { method: 'POST', body: { tenantId } }),
      eventId: 0,
    }),
    removeContractDepartment: async (id, tenantId) => ({
      value: await call<Contract>(`/platform/contracts/${seg(id)}/departments/${seg(tenantId)}`, { method: 'DELETE' }),
      eventId: 0,
    }),
    terminateContract: async (id, endsOn) => ({
      value: await call<Contract>(`/platform/contracts/${seg(id)}/terminate`, { method: 'POST', body: { endsOn } }),
      eventId: 0,
    }),
    fleetCounts: (month) => call<FleetCounts>(`/platform/fleet-counts?month=${encodeURIComponent(month)}`),
    billing: (month) => call<Billing>(`/platform/billing?month=${encodeURIComponent(month)}`),
    billingCsv: (month) => text(`/platform/billing.csv?month=${encodeURIComponent(month)}`),
  };

  return {
    kind: 'api',
    me: () => call<Me>('/me'),
    forTenant(tenantId) {
      let s = sources.get(tenantId);
      if (!s) {
        s = createTenantSource(tenantId);
        sources.set(tenantId, s);
      }
      return s;
    },
    platform,
    // docs/api.md, "Neuigkeiten": ids and dates; the words are in the bundle (news/news.ts).
    announcements: async () => (await call<{ announcements: AnnouncementRef[] }>('/me/announcements')).announcements,
    welcomeDue: async () => (await call<{ due: boolean }>('/me/welcome')).due === true,
    finishWelcome: async () => {
      await call<{ due: boolean }>('/me/welcome', { method: 'POST' });
    },
    acknowledgeAnnouncements: async (ids) => {
      await call<void>('/me/announcements/ack', { method: 'POST', body: { ids: [...ids] } });
    },
    // Profile picture (0014): JSON with base64, which both platforms' fetch send reliably.
    setAvatar: (jpegBase64) => call<Me>('/me/avatar', { method: 'PUT', body: { jpeg: jpegBase64 } }),
    removeAvatar: () => call<Me>('/me/avatar', { method: 'DELETE' }),
    // Further sign-in addresses (0023), the person's own.
    myEmails: () => call<MyEmails>('/me/emails'),
    addMyEmail: (email) => call<MyEmails>('/me/emails', { method: 'POST', body: { email } }),
    confirmMyEmail: (code) => call<MyEmails>('/me/emails/confirm', { method: 'POST', body: { code } }),
    removeMyEmail: (id) => call<MyEmails>(`/me/emails/${seg(id)}`, { method: 'DELETE' }),
    dispose() {
      drawings.dispose();
      drawingCache.clear();
      imageDamage.clear();
    },
  };
}
